import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, UserCheck, Clock, AlertCircle, ChevronUp, ChevronDown, TrendingUp, Percent } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';

// Hard-coded total pilgrims data
const totalPilgrimsData = {
  'France': 0,
  'UK': 0,
  'Italy': 0,
  'Netherlands': 0,
  'USA': 0,
  'Canada': 0,
  'Belgium': 0,
  'Germany': 0,
  'Switzerland': 0,
  'Sweden': 0,
  'Hong Kong - China': 0,
  'Others': 0
};

export const LinkingComparison = () => {
  const { translations } = useLanguage();
  const { links } = usePilgrimGuideStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  
  // Filter only approved links
  const approvedLinks = Object.values(links).filter(link => link.status === 'approved');

  // Calculate linked pilgrims and disputes by country
  const linkedPilgrimsByCountry = approvedLinks.reduce((acc, link) => {
    const country = totalPilgrimsData.hasOwnProperty(link.residenceCountry) 
      ? link.residenceCountry 
      : 'Others';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Calculate disputes by country
  const disputesByCountry = Object.values(links)
    .filter(link => link.status === 'disputed')
    .reduce((acc, link) => {
      const country = totalPilgrimsData.hasOwnProperty(link.residenceCountry) 
        ? link.residenceCountry 
        : 'Others';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  // Calculate totals
  const totalPilgrims = Object.values(totalPilgrimsData).reduce((a, b) => a + b, 0);
  const totalLinked = Object.values(linkedPilgrimsByCountry).reduce((a, b) => a + b, 0);
  const totalDisputes = Object.values(disputesByCountry).reduce((a, b) => a + b, 0);
  
  // Calculate overall percentage including both linked and disputed pilgrims
  const totalProgress = totalLinked + Math.floor(totalDisputes / 2);
  const overallPercentage =
    totalPilgrims > 0 ? ((totalProgress / totalPilgrims) * 100).toFixed(1) : '0.0';

  // Sort countries by linked pilgrims count (descending)
  const sortedCountries = Object.entries(totalPilgrimsData)
    .map(([country, total]) => {
      const linked = linkedPilgrimsByCountry[country] || 0;
      const disputes = Math.floor((disputesByCountry[country] || 0) / 2);
      const totalProgress = linked + disputes;
      return {
        country,
        total,
        linked,
        disputes,
        percentage: total > 0 ? (totalProgress / total) * 100 : 0
      };
    })
    .sort((a, b) => b.linked - a.linked);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-50 rounded-xl">
              <Users className="text-primary-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {translations?.linkingComparison?.title || 'Pilgrims Distribution by Country'}
              </h2>
              <p className="text-gray-500 mt-1">
                {translations?.linkingComparison?.subtitle || 'Comparison between total pilgrims and approved links'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary-50 p-6 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Users size={24} className="text-primary-500" />
              <span className="font-medium text-primary-700 text-lg">Total Pilgrims</span>
            </div>
            <div className="text-4xl font-bold text-primary-600">{totalPilgrims.toLocaleString()}</div>
          </div>

          <div className="bg-green-50 p-6 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={24} className="text-green-500" />
              <span className="font-medium text-green-700 text-lg">Total Progress</span>
            </div>
            <div className="text-4xl font-bold text-green-600">{totalProgress.toLocaleString()}</div>
            <div className="text-sm text-green-600 mt-1">
              {totalLinked.toLocaleString()} linked + {Math.floor(totalDisputes / 2).toLocaleString()} disputes
            </div>
          </div>

          <div className="bg-blue-50 p-6 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Percent size={24} className="text-blue-500" />
              <span className="font-medium text-blue-700 text-lg">Overall Progress</span>
            </div>
            <div className="text-4xl font-bold text-blue-600">{overallPercentage}%</div>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Total Pilgrims
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Approved Links
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Disputes
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Progress
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCountries.map(({ country, total, linked, disputes, percentage }) => (
                <motion.tr
                  key={country}
                  onMouseEnter={() => setHoveredCountry(country)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  className="hover:bg-gray-50 transition-colors"
                  initial={false}
                  animate={{
                    backgroundColor: hoveredCountry === country ? 'rgba(249, 250, 251, 1)' : 'rgba(255, 255, 255, 1)',
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-base font-medium text-gray-900">{country}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-xl font-semibold text-gray-900">
                      {total.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-xl font-semibold text-primary-600">
                      {linked.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-xl font-semibold text-red-600">
                      {disputes.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-3">
                      <span className="text-lg font-semibold text-gray-900">
                        {percentage.toFixed(1)}%
                      </span>
                      <div className="w-32 bg-gray-200 rounded-full h-3">
                        <motion.div
                          className={`h-full rounded-full transition-all ${
                            percentage >= 70 ? 'bg-green-500' :
                            percentage >= 30 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {/* Total Row */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-xl font-bold text-gray-900">Total</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span className="text-2xl font-bold text-gray-900">
                    {totalPilgrims.toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span className="text-2xl font-bold text-primary-600">
                    {totalLinked.toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span className="text-2xl font-bold text-red-600">
                    {Math.floor(totalDisputes / 2).toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center justify-end gap-3">
                    <span className="text-xl font-bold text-gray-900">
                      {overallPercentage}%
                    </span>
                    <div className="w-32 bg-gray-200 rounded-full h-3">
                      <motion.div
                        className="h-3 rounded-full bg-primary-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${overallPercentage}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};
